import { HttpClient } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import * as countryData from "country-telephone-data";
import * as L from "leaflet";
import "leaflet-routing-machine";
import { BehaviorSubject, Observable, firstValueFrom } from "rxjs";
import { map, timeout } from "rxjs/operators";
import { environment } from "src/environments/environment";

import { LocationAccuracy } from "@awesome-cordova-plugins/location-accuracy/ngx";
import { Geolocation } from "@capacitor/geolocation";
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from "capacitor-native-settings";
import { ClientPosition, Position, SearchPosition } from "../helpers/types";
import { AppService } from "./app.service";
import { LocalStorageService } from "./localStorage.service";
import { LocationService } from "./location.service";
import { MapKitService } from "./mapkit.service";

@Injectable({
  providedIn: "root",
})
export class osService {
  url = `${environment.apiUrl}`;
  hostUrl = `${environment.imagePath}`;
  authUrl = `${environment.authUrl}`;
  googleMapKey = environment.map_key;
  private router: any;

  countries: any[] = [];
  private mapKitServiceInstance: MapKitService | null = null;

  // Cache frontend pour les routes (complément au cache backend)
  private routeCache = new Map<string, {
    data: { distance: number; duration: number; path: { lat: number; lng: number }[] };
    timestamp: number;
  }>();
  private readonly ROUTE_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  private readonly ROUTE_CACHE_MAX_SIZE = 50; // Limite de 50 routes en cache
  private readonly ROUTE_REQUEST_TIMEOUT_MS = 6000;

  constructor(
    private http: HttpClient,
    private appService: AppService,
    private localStorageService: LocalStorageService,
    private locationService: LocationService,
    private locationAccuracy: LocationAccuracy,
    private injector: Injector
  ) {

    this.router = L.Routing.osrmv1({
      serviceUrl: "https://router.project-osrm.org/route/v1",
    });


  }

  private getMapKitService(): MapKitService {
    if (!this.mapKitServiceInstance) {
      this.mapKitServiceInstance = this.injector.get(MapKitService);
    }
    return this.mapKitServiceInstance;
  }

  /*==============================
 BehaviorSubject
================================*/

  private userLocationSubject = new BehaviorSubject<Position>({
    label: "",
    coords: {
      latitude: 0,
      longitude: 0,
    },
  });
  private SearchLocationSubject = new BehaviorSubject<
    SearchPosition | null | undefined
  >({
    title: "",
    rue: "",
    country: "",
    pays: "",
    position: [
      {
        lat: 0,
        long: 0,
      },
    ],
  });
  private markerLocationSubject = new BehaviorSubject<Position>({
    label: "",
    coords: {
      latitude: 0,
      longitude: 0,
    },
  });

  userLocation$ = this.userLocationSubject.asObservable();
  SearchLocationSubject$ = this.SearchLocationSubject.asObservable();
  markerLocationSubject$ = this.markerLocationSubject.asObservable();

  setUserLocation(userLocation: Position) {
    this.userLocationSubject.next(userLocation);
  }
  setMarkerLocation(markerLocationSubject: Position) {
    this.markerLocationSubject.next(markerLocationSubject);
  }
  setSearchLocation(SearchLocationSubject: SearchPosition | null) {
    this.SearchLocationSubject.next(SearchLocationSubject);
  }
  removeSearchLocation(SearchLocationSubject: SearchPosition) {
    this.SearchLocationSubject.next(SearchLocationSubject);
  }

  /*==============================
get countries
================================*/

  fetchCountryData(): Observable<any[]> {
    return this.http.get<any[]>("https://restcountries.com/v3.1/all").pipe(
      map((res: any[]) => {
        return res
          .filter(
            (country: any) =>
              country.region === "Europe" || country.region === "Africa"
          )
          .map((country: any) => {
            let dialCode =
              country.callingCodes && country.callingCodes.length > 0
                ? "+" + country.callingCodes[0]
                : "N/A";
            return {
              name: country.name.common,
              code: country.cca2,
              flag: country.flags.png,
            };
          });
      })
    );
  }

  fetchEuropsCountryData(): Observable<any[]> {
    return this.http
      .get<any[]>("https://restcountries.com/v3.1/region/europe")
      .pipe(
        map((res: any[]) => {
          return res.map((country: any) => {
            return {
              name: country.name.common,
              code: country.cca2,
              flag: country.flags.png,
            };
          });
        })
      );
  }

  fetchEuropsCountryFromJson(): Observable<any[]> {
    return this.http
      .get<any[]>("../../assets/settings/europsCountries.json")
      .pipe(
        map((res: any[]) => {
          return res.map((country: any) => {
            return {
              name: country.name.common,
              code: country.cca2,
              flag: country.flags.png,
            };
          });
        })
      );
  }

  getCountriesWithPhoneCodes() {
    const countries = countryData.allCountries.map((res: any) => {
      return {
        name: res.name,
        code: res.iso2,
        dialCode: "+" + res.dialCode,
      };
    });
    return countries;
  }

  getCountriesWithFlag(): Observable<any[]> {
    return new Observable((observer) => {
      this.fetchEuropsCountryFromJson().subscribe((data: any[]) => {
        const array1 = data;
        const array2 = this.getCountriesWithPhoneCodes();
        const mergedArray = array1.map((item) => {
          const country2 = array2.find(
            (c) =>
              c.code.toString().toLowerCase() ===
              item.code.toString().toLowerCase()
          );
          if (country2) {
            item.dialCode = country2.dialCode; // Assign dialCode from array2 to array1
          }
          return item;
        });
        observer.next(mergedArray);
        observer.complete();
      });
    });
  }

  getCurrentLocation(): Promise<Position> {
    return new Promise((resolve, reject) => {
      const settings = this.localStorageService.getSettings();
      const map_provider_mobile = settings?.map_provider_mobile || "OPENROUTESERVICE";
      Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0
      })
        .then((resp) => {
          const coords = {
            coords: {
              latitude: resp.coords.latitude,
              longitude: resp.coords.longitude,
            },
            label: "",
          };
          if (map_provider_mobile === "GOOGLEMAP") {
            this.locationService
              .geoCode(resp.coords.latitude, resp.coords.longitude)
              .then((response: any) => {
                coords.label = response.label;
                this.appService.dispatchModal("DETECT_LOCATION_MODAL", false);
                this.setUserLocation(response);
              });
          } else if (map_provider_mobile === "MAPKIT") {
            this.mapkitGeoCode(resp.coords.latitude, resp.coords.longitude);
          } else {
            this.osmapGeoCode(resp.coords.latitude, resp.coords.longitude);
          }
          this.validateLocation(coords);
          resolve(coords);
        })
        .catch(async (error) => {
          this.enableGps();
          reject(error);
        });
    });
  }

  openSettings(app = false) {
    return NativeSettings.open({
      optionAndroid: app
        ? AndroidSettings.ApplicationDetails
        : AndroidSettings.Location,
      optionIOS: app ? IOSSettings.App : IOSSettings.LocationServices,
    });
  }

  async enableGps() {
    const canRequest = await this.locationAccuracy.canRequest();
    if (canRequest) {
      await this.locationAccuracy.request(
        this.locationAccuracy.REQUEST_PRIORITY_HIGH_ACCURACY
      );
      this.getCurrentLocation();
    } else {
      this.openSettings(true);
    }
  }

  osmapGeoCode(latitude: any, longitude: any) {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`;
      this.http.get<any[]>(url).subscribe((res: any) => {
        let addressParts: string[] = [];
        // country, city, county
        const adress = res.address;

        const country = adress.country;
        country && addressParts.push(country);
        const city = adress.city
          ? adress.city
          : adress.village
            ? adress.village
            : null;
        city && addressParts.push(city);
        const county = adress.county
          ? adress.county
          : adress.state
            ? adress.state
            : null;
        county && addressParts.push(county);
        const newAddress = addressParts.join(", ");

        const coords = {
          coords: {
            latitude: latitude,
            longitude: longitude,
          },
          label: newAddress,
        };
        this.validateLocation(coords);
      });
    } catch (err: any) {
      console.error("err : ", err);
    }
  }

  mapkitGeoCode(latitude: any, longitude: any) {
    try {
      this.getMapKitService()
        .reverseGeocode({ latitude, longitude })
        .then((results: any[]) => {
          if (results && results.length > 0) {
            const result = results[0];
            const formattedAddress = result.formattedAddress || "";

            const coords = {
              coords: {
                latitude: latitude,
                longitude: longitude,
              },
              label: formattedAddress,
            };
            this.validateLocation(coords);
          } else {
            // Fallback si aucun résultat
            const coords = {
              coords: {
                latitude: latitude,
                longitude: longitude,
              },
              label: "",
            };
            this.validateLocation(coords);
          }
        })
        .catch((error: any) => {
          console.error("MapKit geocoding error:", error);
          // Fallback en cas d'erreur
          const coords = {
            coords: {
              latitude: latitude,
              longitude: longitude,
            },
            label: "",
          };
          this.validateLocation(coords);
        });
    } catch (err: any) {
      console.error("err : ", err);
      const coords = {
        coords: {
          latitude: latitude,
          longitude: longitude,
        },
        label: "",
      };
      this.validateLocation(coords);
    }
  }
  validateLocation(coords: Position) {
    this.checkRegion({
      lat: coords.coords.latitude,
      lng: coords.coords.longitude,
    }).subscribe((res: any) => {
      if (res.region_exists) {
        this.appService.dispatchModal("DETECT_LOCATION_MODAL", false);
        this.setUserLocation(coords);
        this.localStorageService.setLocation(coords);
      }
      this.appService.dispatchModal("UNSUPPORTED_LOCATION_MODAL", !res.region_exists);
    });
  }

  getOsmapGeoCode(latitude: any, longitude: any): Promise<Position> {
    return new Promise(async (resolve, reject) => {
      try {
        // Essayer d'abord MapKit si disponible
        try {
          const mapKitService = this.getMapKitService();
          if (mapKitService && mapKitService.isMapKitInitialized()) {
            const results = await mapKitService.reverseGeocode({ latitude, longitude });
            if (results && results.length > 0) {
              const result = results[0];
              const coords = {
                more: {
                  title: result.thoroughfare || "",
                  country_code: result.countryCode || "",
                  postcode: "",
                  state: result.administrativeArea || "",
                  road: result.thoroughfare || "",
                  addresstype: "",
                  type: "",
                },
                coords: {
                  latitude: latitude,
                  longitude: longitude,
                },
                label: result.formattedAddress || "",
              };
              resolve(coords);
              return;
            }
          }
        } catch (mapKitError) {
          console.warn('MapKit reverse geocode failed, trying backend:', mapKitError);
        }

        // Essayer l'endpoint backend pour éviter CORS
        try {
          const payload = {
            lat: latitude,
            long: longitude,
          };
          const backendResponse = await firstValueFrom(
            this.http.post<any>(`${this.url}/latlong2adrs`, payload)
          );

          if (backendResponse?.addresse) {
            const coords = {
              more: {
                title: "",
                country_code: "",
                postcode: "",
                state: "",
                road: "",
                addresstype: "",
                type: "",
              },
              coords: {
                latitude: latitude,
                longitude: longitude,
              },
              label: backendResponse.addresse,
            };
            resolve(coords);
            return;
          }
        } catch (backendError) {
          console.warn('Backend reverse geocode failed, trying Nominatim:', backendError);
        }

        // Fallback sur Nominatim (peut avoir des problèmes CORS)
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`;
        this.http.get<any>(url).subscribe({
          next: (res: any) => {
            if (res && res.address) {
              let addressParts: string[] = [];
              const adress = res.address;

              const country = adress.country;
              country && addressParts.push(country);
              const city = adress.city
                ? adress.city
                : adress.village
                  ? adress.village
                  : null;
              city && addressParts.push(city);
              const county = adress.county
                ? adress.county
                : adress.state
                  ? adress.state
                  : null;
              county && addressParts.push(county);
              const newAddress = addressParts.join(", ");
              const more = {
                title: adress.amenity || "",
                country_code: adress.country_code,
                postcode: adress.postcode,
                state: adress.state,
                road: adress.road,
                addresstype: res.addresstype,
                type: res.type,
              };
              const coords = {
                more,
                coords: {
                  latitude: latitude,
                  longitude: longitude,
                },
                label: newAddress,
              };
              resolve(coords);
            } else {
              // Si aucune adresse trouvée, retourner une position avec label vide
              const coords = {
                more: {
                  title: "",
                  country_code: "",
                  postcode: "",
                  state: "",
                  road: "",
                  addresstype: "",
                  type: "",
                },
                coords: {
                  latitude: latitude,
                  longitude: longitude,
                },
                label: "",
              };
              resolve(coords);
            }
          },
          error: (error: any) => {
            console.error("Nominatim reverse geocode error (CORS possible):", error);
            // En cas d'erreur CORS ou autre, retourner une position avec les coordonnées mais sans adresse
            const coords = {
              more: {
                title: "",
                country_code: "",
                postcode: "",
                state: "",
                road: "",
                addresstype: "",
                type: "",
              },
              coords: {
                latitude: latitude,
                longitude: longitude,
              },
              label: "",
            };
            resolve(coords); // Résoudre plutôt que rejeter pour permettre la continuation
          }
        });
      } catch (err: any) {
        console.error("getOsmapGeoCode error:", err);
        // En cas d'erreur, retourner une position minimale
        const coords = {
          more: {
            title: "",
            country_code: "",
            postcode: "",
            state: "",
            road: "",
            addresstype: "",
            type: "",
          },
          coords: {
            latitude: latitude,
            longitude: longitude,
          },
          label: "",
        };
        resolve(coords);
      }
    });
  }

  autocomplete(term: string) {
    const params = {
      term: term,
    };

    return this.http.post<any[]>(
      `${this.url}/frontoffice/auto-complete`,
      params
    );
  }

  autocompleteStripe(term: string) {
    const params = {
      term: term,
    };

    return this.http.post<{
      success: boolean;
      count: number;
      data: Array<{
        title: string | null;
        street: string | null;
        zip_code: string | null;
        city: string | null;
        country: string | null;
        position: { lat: number | null; long: number | null } | null;
      }>;
    }>(`${this.url}/frontoffice/auto-complete-stripe`, params);
  }

  savePrestatairePosition(data: { long: number; lat: number }) {
    return this.http.post<any[]>(
      `${this.authUrl}/frontoffice/send_prestataire_position`,
      data
    );
  }

  saveClientPosition(data: ClientPosition) {
    return this.http.post<any[]>(`${this.authUrl}/frontoffice/adresses`, data);
  }

  checkRegion(data: { lat: number; lng: number }) {
    return this.http.post<any[]>(
      `${this.url}/check-region-exists`,
      data
    );
  }

  getRegionsList() {
    return this.http.get<any[]>(`${this.authUrl}/frontoffice/areas`);
  }
  convertDistance(distanceInMeters: number): { Km: number; M: number } {
    const distanceInKm = distanceInMeters / 1000;
    const kmPart = Math.floor(distanceInKm);
    const mPart = Math.round(distanceInMeters % 1000);

    return {
      Km: kmPart,
      M: mPart
    };
  }

  /**
   * Standardise le calcul de distance pour assurer la cohérence entre les différents services
   */
  standardizeDistance(distanceInMeters: number): { Km: number; M: number } {
    // Arrondir à la dizaine de mètres la plus proche pour réduire les variations
    const roundedDistance = Math.round(distanceInMeters / 10) * 10;
    return this.convertDistance(roundedDistance);
  }

  /**
   * Compare deux distances et retourne la différence en mètres
   */
  compareDistances(distance1: number, distance2: number): number {
    return Math.abs(distance1 - distance2);
  }

  getDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): Promise<number> {
    return new Promise((resolve, reject) => {
      // Create latLng objects
      const start = L.latLng(lat1, lon1);
      const end = L.latLng(lat2, lon2);

      // Validate the start and end points
      if (!this.router || typeof this.router.route !== "function") {
        return reject(new Error("Router is not initialized correctly."));
      }
      try {
        this.router.route([start, end], (error: any, routes: any) => {
          if (error) {
            console.error("Routing error:", error);
            return reject(error);
          }
          if (routes && routes.length > 0) {
            const distance = routes[0].summary?.totalDistance ?? 0; // Distance in meters
            resolve(distance);
          } else {
            reject(new Error("No routes found"));
          }
        });
      } catch (error) {
        // Call the router
        console.error("error == ", error);
      }
    });
  }
  getDistance2(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): Promise<number> {
    return new Promise((resolve, reject) => {
      // Construct the OSRM URL
      const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${lon1},${lat1};${lon2},${lat2}?overview=false`;

      // Make the HTTP GET request to the OSRM API
      this.http.get<any>(osrmUrl).subscribe({
        next: (response) => {
          if (response.routes && response.routes.length > 0) {
            const distance = response.routes[0].legs[0].distance; // Distance in meters
            resolve(distance);
          } else {
            reject(new Error("No routes found in the response."));
          }
        },
        error: (error) => {
          console.error("HTTP Error:", error);
          reject(new Error("Failed to fetch distance from OSRM."));
        },
      });
    });
  }

  /**
   * Génère une clé de cache pour une route
   */
  private getRouteCacheKey(lat1: number, lon1: number, lat2: number, lon2: number): string {
    // Arrondir les coordonnées à 4 décimales (~11 mètres de précision) pour éviter trop de variations
    const round = (n: number) => Math.round(n * 10000) / 10000;
    return `route_${round(lat1)}_${round(lon1)}_${round(lat2)}_${round(lon2)}`;
  }

  /**
   * Nettoie le cache des routes expirées
   */
  private cleanRouteCache(): void {
    const now = Date.now();
    for (const [key, value] of this.routeCache.entries()) {
      if (now - value.timestamp > this.ROUTE_CACHE_TTL_MS) {
        this.routeCache.delete(key);
      }
    }

    // Si le cache est trop grand, supprimer les entrées les plus anciennes
    if (this.routeCache.size > this.ROUTE_CACHE_MAX_SIZE) {
      const entries = Array.from(this.routeCache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = entries.slice(0, this.routeCache.size - this.ROUTE_CACHE_MAX_SIZE);
      toRemove.forEach(([key]) => this.routeCache.delete(key));
    }
  }

  /**
   * Récupère la géométrie d'un itinéraire simple (origin -> destination)
   * Utilise l'endpoint /route/provider-to-client
   * 
   * Cache frontend (complément au cache backend) :
   * - TTL: 5 minutes
   * - Max: 50 routes
   * - Précision: ~11 mètres (arrondi à 4 décimales)
   * 
   * NOTE: Le cache backend reste la solution principale pour la scalabilité.
   * Ce cache frontend améliore uniquement la réactivité pour les requêtes répétées.
   */
  async getRouteGeometry(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): Promise<{ distance: number; duration: number; path: { lat: number; lng: number }[] }> {
    // Nettoyer le cache périodiquement
    this.cleanRouteCache();

    // Vérifier le cache frontend
    const cacheKey = this.getRouteCacheKey(lat1, lon1, lat2, lon2);
    const cached = this.routeCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < this.ROUTE_CACHE_TTL_MS) {
      return cached.data;
    }

    const payload = {
      origin: { latitude: lat1, longitude: lon1 },
      destination: { latitude: lat2, longitude: lon2 },
    };

    try {
      // Essayer d'abord l'endpoint principal
      const response = await firstValueFrom(
        this.http
          .post<any>(`${this.url}/frontoffice/route/provider-to-client`, payload)
          .pipe(timeout(this.ROUTE_REQUEST_TIMEOUT_MS))
      );

      // Vérifier différentes structures de réponse possibles
      let routeData: { distance: number; duration: number; path: { lat: number; lng: number }[] } | null = null;

      if (
        response?.distance !== undefined &&
        response?.duration !== undefined &&
        Array.isArray(response?.path) &&
        response.path.length > 0
      ) {
        routeData = {
          distance: response.distance,
          duration: response.duration,
          path: response.path,
        };
      } else if (response?.routes && Array.isArray(response.routes) && response.routes.length > 0) {
        // Vérifier si la réponse contient un tableau de routes
        const route = response.routes[0];
        if (
          route?.distance !== undefined &&
          route?.duration !== undefined &&
          Array.isArray(route?.path) &&
          route.path.length > 0
        ) {
          routeData = {
            distance: route.distance,
            duration: route.duration,
            path: route.path,
          };
        } else if (route.geometry?.coordinates) {
          // Vérifier la structure avec geometry
          const path = route.geometry.coordinates.map((coord: [number, number]) => ({
            lng: coord[0],
            lat: coord[1],
          }));
          routeData = {
            distance: route.distance || 0,
            duration: route.duration || 0,
            path,
          };
        }
      } else if (response?.route) {
        // Vérifier si la réponse contient directement une route
        const route = response.route;
        if (route.geometry?.coordinates) {
          const path = route.geometry.coordinates.map((coord: [number, number]) => ({
            lng: coord[0],
            lat: coord[1],
          }));
          routeData = {
            distance: route.distance || 0,
            duration: route.duration || 0,
            path,
          };
        }
      }

      if (routeData) {
        // Mettre en cache le résultat
        this.routeCache.set(cacheKey, {
          data: routeData,
          timestamp: Date.now()
        });
        return routeData;
      }

      throw new Error("No routes found in backend response.");
    } catch (error: any) {
      // Fallback sur /route/osrm si l'endpoint principal échoue
      try {
        const osrmPayload = {
          ...payload,
          overview: "full",
        };
        const osrmResponse = await firstValueFrom(
          this.http
            .post<any>(`${this.url}/frontoffice/route/osrm`, osrmPayload)
            .pipe(timeout(this.ROUTE_REQUEST_TIMEOUT_MS))
        );


        let fallbackRouteData: { distance: number; duration: number; path: { lat: number; lng: number }[] } | null = null;

        if (
          osrmResponse?.distance !== undefined &&
          osrmResponse?.duration !== undefined &&
          Array.isArray(osrmResponse?.path) &&
          osrmResponse.path.length > 0
        ) {
          fallbackRouteData = {
            distance: osrmResponse.distance,
            duration: osrmResponse.duration,
            path: osrmResponse.path,
          };
        }

        // Vérifier si la réponse contient un tableau de routes
        if (osrmResponse?.routes && Array.isArray(osrmResponse.routes) && osrmResponse.routes.length > 0) {
          const route = osrmResponse.routes[0];
          if (route.geometry?.coordinates) {
            const path = route.geometry.coordinates.map((coord: [number, number]) => ({
              lng: coord[0],
              lat: coord[1],
            }));
            fallbackRouteData = {
              distance: route.distance || 0,
              duration: route.duration || 0,
              path,
            };
          }
        }

        if (!fallbackRouteData && osrmResponse?.route?.geometry?.coordinates) {
          const route = osrmResponse.route;
          const path =
            route.geometry.coordinates.map((coord: [number, number]) => ({
              lng: coord[0],
              lat: coord[1],
            })) || [];
          fallbackRouteData = {
            distance: route.distance || 0,
            duration: route.duration || 0,
            path,
          };
        }

        if (fallbackRouteData) {
          // Mettre en cache le résultat du fallback aussi
          this.routeCache.set(cacheKey, {
            data: fallbackRouteData,
            timestamp: Date.now()
          });
          return fallbackRouteData;
        }

        throw new Error("No routes found in OSRM fallback response.");
      } catch (fallbackError) {
        console.error("Failed to fetch route geometry from backend.", fallbackError);
        throw fallbackError;
      }
    }
  }

  /**
   * Récupère la géométrie d'un itinéraire avec waypoint (transporter -> pickup -> destination)
   * Utilise l'endpoint /route/provider-to-client/points
   */
  async getRouteGeometryWithPoints(
    transporter: { latitude: number; longitude: number },
    pickup: { latitude: number; longitude: number },
    destination: { latitude: number; longitude: number }
  ): Promise<{ distance: number; duration: number; path: { lat: number; lng: number }[] }> {
    const payload = {
      transporter,
      pickup,
      destination,
    };

    try {
      const response = await firstValueFrom(
        this.http
          .post<any>(`${this.url}/frontoffice/route/provider-to-client/points`, payload)
          .pipe(timeout(this.ROUTE_REQUEST_TIMEOUT_MS))
      );

      // Vérifier différentes structures de réponse possibles
      if (
        response?.distance !== undefined &&
        response?.duration !== undefined &&
        Array.isArray(response?.path) &&
        response.path.length > 0
      ) {
        return {
          distance: response.distance,
          duration: response.duration,
          path: response.path,
        };
      }

      // Vérifier si la réponse contient un tableau de routes
      if (response?.routes && Array.isArray(response.routes) && response.routes.length > 0) {
        const route = response.routes[0];
        if (
          route?.distance !== undefined &&
          route?.duration !== undefined &&
          Array.isArray(route?.path) &&
          route.path.length > 0
        ) {
          return {
            distance: route.distance,
            duration: route.duration,
            path: route.path,
          };
        }
        // Vérifier la structure avec geometry
        if (route.geometry?.coordinates) {
          const path = route.geometry.coordinates.map((coord: [number, number]) => ({
            lng: coord[0],
            lat: coord[1],
          }));
          return {
            distance: route.distance || 0,
            duration: route.duration || 0,
            path,
          };
        }
      }

      // Vérifier si la réponse contient directement une route
      if (response?.route) {
        const route = response.route;
        if (route.geometry?.coordinates) {
          const path = route.geometry.coordinates.map((coord: [number, number]) => ({
            lng: coord[0],
            lat: coord[1],
          }));
          return {
            distance: route.distance || 0,
            duration: route.duration || 0,
            path,
          };
        }
      }
      throw new Error("No routes found in backend response.");
    } catch (error) {
      throw error;
    }
  }
}
